/* eslint-disable camelcase */

/**
 * 🚧 GTM CONTEXT STAGING - Migration in Progress
 *
 * Staging implementation of new GA4 user context events.
 * Logs to console + stagingDataLayer instead of GTM until validated.
 *
 * FEATURE FLAG: GTM_EVENTS_MIGRATION_LOGS_ACTIVATED
 */

import { useRuntimeConfig } from '#imports'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useSessionId } from '@backmarket/nuxt-module-identification/useSessionId'
import { useVisitorId } from '@backmarket/nuxt-module-identification/useVisitorId'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { toPosixLocale } from '@backmarket/utils/string/toPosixLocale'

import { useLegalStore } from '../../../composables/useLegalStore'
import type { UserContext } from '../../../types'

import { pushEventToStagingDataLayer } from './events.utils'

function getFormattedDate(date?: string | null): string | null {
  if (!date) return null

  const timestamp = Date.parse(date)
  if (Number.isNaN(timestamp)) return null

  return new Date(timestamp).toISOString().split('T')[0].replace(/-/g, '')
}

export type AmplitudeUserContextParams = {
  amplitudeDeviceId: string | undefined
  amplitudeSessionId: string | undefined
  amplitudeUserId: string | undefined
}

export function setAmplitudeUserContext(
  params: AmplitudeUserContextParams,
): void {
  const event = {
    event: 'user_data',
    user_ids: {
      amplitude_device_id: params.amplitudeDeviceId,
      amplitude_session_id: params.amplitudeSessionId,
      amplitude_user_id: params.amplitudeUserId,
    },
  }

  pushEventToStagingDataLayer(event)
}

export function setUserContext(userContext: UserContext): void {
  const { market } = useMarketplace()
  const {
    public: { tracking: config },
  } = useRuntimeConfig()

  const bm_visitor_id = useVisitorId()
  const bm_session_id = useSessionId()
  const locale = toPosixLocale(useI18nLocale())

  const legalStore = useLegalStore()
  const { consent } = legalStore

  const isLoggedIn = Boolean(userContext.userId)

  // session_context — always present
  const session_context = {
    bm_visitor_id,
    bm_session_id,
    app_version: config.version,
    currency: market.defaultCurrency,
    market: market.countryCode,
    locale,
  }

  // user_ids — BM IDs only, logged-in only (amplitude IDs sent via setAmplitudeUserContext)
  const user_ids = isLoggedIn
    ? {
        bm_account_id: userContext.clientId,
        user_id: userContext.userId,
        bm_user_id: userContext.bmUserId,
      }
    : undefined

  // user_behavior — login_status always present; other fields only when logged in
  const user_behavior = isLoggedIn
    ? {
        login_status: 'logged_in',
        user_type: getFormattedDate(userContext.lastOrderDate)
          ? 'returning'
          : 'new',
        account_creation_date: getFormattedDate(
          userContext.accountCreationDate,
        ),
        number_of_orders: userContext.numberOfOrders,
        last_order_date: getFormattedDate(userContext.lastOrderDate),
        customer_lifetime_value: parseFloat(
          userContext.lifetimeRevenue.amount ?? '0',
        ),
      }
    : { login_status: 'logged_out' }

  // user_info — logged-in only, with SHA-256 hashed PII
  const user_info = isLoggedIn
    ? {
        email_address: userContext.emailHashed,
      }
    : undefined

  // TODO [BB-1.5]: user_info fields (first_name, last_name, phone_number, country, region, city, postal_code)
  //               are not available in UserContext — needs new fields from apps

  const experiments = useExperiments()
  const experiment_context = Object.entries(experiments).map(
    ([experiment_id, variant]) => ({ experiment_id, variant }),
  )

  const event = {
    event: 'user_data',
    session_context,
    ...(user_info ? { user_info } : {}),
    ...(user_ids ? { user_ids } : {}),
    user_behavior,
    consent_status: {
      advertising: consent.advertising,
      analytics: consent.analytics,
      functional: consent.functional,
      user_experience: consent.userExperience,
    },
    ...(experiment_context.length > 0 ? { experiment_context } : {}),
  }

  pushEventToStagingDataLayer(event)
}
